<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { NEmpty, NSpin } from 'naive-ui'
import { useMemoryEntriesStore, type MemoryEntry } from '../stores/memory_entries'
import { ALL_TYPES, memoryEntryKey, memoryTypeTone, shortDate, type MemorySort } from './MemoryEntryMeta'

// Entries column of the Memory browser: text filter, type chips, sort,
// then one row per entry (type pill + date, mono slug, description).
// Owns the list's load / error / empty branches so the detail column
// beside it can render an entry even while the list is still loading.

const props = defineProps<{
  entries: MemoryEntry[]
  types: string[]
  selectedKey: string | null
  // Rows name their scope only when the list spans every scope.
  showScope: boolean
  linkFor: (entry: MemoryEntry) => RouteLocationRaw
}>()

const type = defineModel<string>('type', { required: true })
const query = defineModel<string>('query', { required: true })
const sort = defineModel<MemorySort>('sort', { required: true })

const store = useMemoryEntriesStore()

const typeChips = computed(() => [ALL_TYPES, ...props.types])
const noMatches = computed(() => store.totalCount > 0 && props.entries.length === 0)

const CHIP = 'min-h-8 rounded-full border px-3 text-[13px] transition-colors max-sm:min-h-11'
const CHIP_ON = 'border-brand-primary bg-brand-primary/10 text-ink'
const CHIP_OFF = 'border-border-control text-ink-muted hover:text-ink'

function clearFilters(): void {
  query.value = ''
  type.value = ALL_TYPES
}
</script>

<template>
  <section aria-label="Entries" class="flex min-w-0 flex-col" data-testid="memory-entry-list">
    <div v-if="store.loading && !store.loaded" class="flex items-center justify-center py-12">
      <NSpin />
    </div>

    <p v-else-if="store.loadError" class="m-0 text-sm text-status-alert" data-testid="memory-load-error">
      Failed to load: {{ store.loadError }}
    </p>

    <NEmpty
      v-else-if="store.totalCount === 0"
      description="No memory entries yet. The brain writes them from Claude Code sessions; you can also add them via the CLI or API."
      class="my-12"
    />

    <template v-else>
      <div class="flex flex-col gap-3 pb-3.5">
        <label data-testid="memory-filter" class="flex flex-col">
          <span class="sr-only">Filter entries</span>
          <input
            v-model="query"
            type="search"
            placeholder="Filter entries…"
            class="min-h-10 rounded-[10px] border border-border-control bg-surface-raised px-3 text-sm text-ink placeholder:text-ink-faint max-sm:min-h-11"
          />
        </label>
        <div role="group" aria-label="Type" class="flex flex-wrap gap-1.5">
          <button
            v-for="chip in typeChips"
            :key="chip"
            type="button"
            data-testid="memory-type-chip"
            :aria-pressed="type === chip"
            :class="[CHIP, type === chip ? CHIP_ON : CHIP_OFF]"
            @click="type = chip"
          >
            {{ chip === ALL_TYPES ? 'All' : chip }}
          </button>
        </div>
        <div role="group" aria-label="Sort" class="flex items-center gap-1 text-xs text-ink-faint">
          <span>Sort</span>
          <button
            v-for="key in ['updated', 'name'] as const"
            :key="key"
            type="button"
            :data-testid="`memory-sort-${key}`"
            :aria-pressed="sort === key"
            class="min-h-8 rounded-md px-2 transition-colors max-sm:min-h-11"
            :class="sort === key ? 'bg-surface-hover text-ink' : 'hover:text-ink'"
            @click="sort = key"
          >
            {{ key === 'updated' ? 'Recent' : 'Name' }}
          </button>
        </div>
      </div>

      <p v-if="noMatches" data-testid="memory-no-matches" class="m-0 py-3 text-sm text-ink-muted">
        No entries match these filters.
        <button type="button" class="min-h-8 text-brand-primary hover:underline" @click="clearFilters">
          Clear filters
        </button>
      </p>

      <ul class="m-0 list-none p-0">
        <li v-for="entry in entries" :key="memoryEntryKey(entry)" class="border-t border-border-divider">
          <RouterLink
            :to="linkFor(entry)"
            data-testid="memory-entry-row"
            :aria-current="memoryEntryKey(entry) === selectedKey ? 'page' : undefined"
            class="flex flex-col gap-1.5 px-3 py-3.5 no-underline transition-colors hover:bg-surface-hover"
            :class="
              memoryEntryKey(entry) === selectedKey
                ? 'bg-surface-raised shadow-[inset_2px_0_0_var(--color-brand-primary)]'
                : ''
            "
          >
            <span class="flex items-center gap-2">
              <span class="rounded-full px-2 py-0.5 text-xs font-semibold" :class="memoryTypeTone(entry.type)">
                {{ entry.type }}
              </span>
              <span v-if="showScope" class="font-mono text-xs text-ink-faint">{{ entry.project }}</span>
              <span class="flex-1" />
              <span class="text-xs text-ink-faint">{{ shortDate(entry.updated_at) }}</span>
            </span>
            <span class="break-words font-mono text-[13px] text-ink" data-testid="memory-entry-name">{{
              entry.name
            }}</span>
            <span v-if="entry.description" class="text-[13px] leading-snug text-ink-muted">
              {{ entry.description }}
            </span>
          </RouterLink>
        </li>
      </ul>
    </template>
  </section>
</template>
